import React, { useContext, useEffect, useState, useRef } from 'react';
import './styles.css';
import { Card, Form, Button, Container } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import useCreateRefund from '../../hook/useCreateRefund';
import useSendOtp from '../../hook/useSendOtp';
import useVerifyOtp from '../../hook/useVerifyOtp';
import { OrderContext } from '../../context/OrderContext';
import { toast } from 'react-toastify';
import ReCAPTCHA from 'react-google-recaptcha';

// Logo dosyasını içe aktar
import logo from '../../assets/img/logo.png';

const Home = () => {
  const navigate = useNavigate();
  const { orderNumber, setOrderNumber, setEmail, email } = useContext(OrderContext);
  const { dataProducts } = useCreateRefund();
  const { sendOtp, loading: sendingOtp } = useSendOtp();
  const { verifyOtp, loading: verifyingOtp } = useVerifyOtp();
  const [captchaToken, setCaptchaToken] = useState(null);
  const [otpSent, setOtpSent] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [otpVerified, setOtpVerified] = useState(false);
  const recaptchaRef = useRef(null);
  const otpSendingRef = useRef(false); // OTP gönderiminin tekrarını önlemek için

  useEffect(() => {
    const savedOrderNumber = sessionStorage.getItem('orderNumber');
    const savedEmail = sessionStorage.getItem('email');

    if (savedOrderNumber && savedEmail) {
      setOrderNumber(savedOrderNumber);
      setEmail(savedEmail);
    }
  }, [setOrderNumber, setEmail]);

  // reCAPTCHA doğrulandığında otomatik olarak OTP gönder
  useEffect(() => {
    if (captchaToken && !otpSent && !otpSendingRef.current) {
      // Sipariş numarası ve email kontrolü
      if (!orderNumber || !email) {
        toast.error('Lütfen önce sipariş numarası ve e-posta adresini girin.');
        setCaptchaToken(null); // reCAPTCHA'yı sıfırla
        if (recaptchaRef.current) {
          recaptchaRef.current.reset();
        }
        return;
      }

      // Flag'i set et - tekrar çalışmasını önle
      otpSendingRef.current = true;

      const sendOtpCode = async () => {
        const success = await sendOtp(orderNumber, email);
        if (success) {
          setOtpSent(true);
          // Başarılı olduğunda flag'i sıfırla (zaten otpSent true olduğu için tekrar çalışmayacak)
          otpSendingRef.current = false;
          toast.success('Doğrulama kodu e-posta adresinize gönderildi.');
        } else {
          toast.error('Doğrulama kodu gönderilemedi. Lütfen tekrar deneyin.');
          setCaptchaToken(null); // reCAPTCHA'yı sıfırla
          if (recaptchaRef.current) {
            recaptchaRef.current.reset();
          }
          // Hata durumunda flag'i sıfırla ki tekrar deneyebilsin
          otpSendingRef.current = false;
        }
      };
      sendOtpCode();
    }
  }, [captchaToken, orderNumber, email, otpSent, sendOtp]);

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    if (!otpCode || otpCode.length !== 6) {
      toast.error('Lütfen 6 haneli doğrulama kodunu girin.');
      return;
    }

    const success = await verifyOtp(orderNumber, email, otpCode);
    if (success) {
      setOtpVerified(true);
      toast.success('E-posta adresi doğrulandı!');
      // OTP doğrulandıktan sonra create_refund çağrısı yap
      handleCreateRefund();
    } else {
      toast.error('Doğrulama kodu hatalı. Lütfen tekrar deneyin.');
    }
  };

  const handleCreateRefund = async () => {
    try {
      const result = await dataProducts(orderNumber, email, captchaToken);
      if (result.UUID) {
        sessionStorage.setItem('orderNumber', orderNumber);
        sessionStorage.setItem('email', email);
        toast.success('Sipariş bulundu!');
        navigate('/return-type');
      } else {
        toast.error('Girilen bilgiler ile sipariş bulunamadı!');
      }
    } catch {
      toast.error('Girilen bilgiler ile sipariş bulunamadı!');
    }
  };

  const handleResendOtp = async () => {
    // Flag'i sıfırla ki tekrar gönderebilsin
    otpSendingRef.current = false;
    const success = await sendOtp(orderNumber, email);
    if (success) {
      setOtpCode('');
      toast.success('Yeni doğrulama kodu e-posta adresinize gönderildi.');
    } else {
      toast.error('Doğrulama kodu gönderilemedi. Lütfen tekrar deneyin.');
    }
  };

  return (
    <Container
      className="d-flex justify-content-center align-items-center home-container"
      style={{ minHeight: '100vh', padding: '2rem 1rem' }}
    >
      <Card className="card-box-1">
        <Card.Body className="d-flex flex-column">
          <Card.Title className="d-flex justify-content-center mb-4">
            <img
              src={logo}
              alt="Logo"
              style={{ width: 160, height: 80 }}
            />
          </Card.Title>

          <Form onSubmit={(e) => e.preventDefault()} className="d-flex flex-column flex-grow-0">
            <Form.Group
              className="custom-input-container"
              controlId="formOrderNumber"
            >
              <Form.Control
                type="text"
                className="custom-placeholder"
                placeholder="Sipariş Numarası"
                value={orderNumber}
                onChange={(e) => setOrderNumber(e.target.value)}
                required
              />
            </Form.Group>

            <Form.Group
              className="custom-input-container"
              controlId="formEmail"
            >
              <Form.Control
                type="email"
                className="custom-placeholder"
                placeholder="E-posta Adresi"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </Form.Group>

            <div className="recaptcha-button-wrapper">
              {!captchaToken ? (
                <div className="recaptcha-wrapper">
                  <ReCAPTCHA
                    ref={recaptchaRef}
                    sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY || ''}
                    onChange={(value) => {
                      // Sipariş numarası ve email kontrolü
                      if (!orderNumber || !email) {
                        toast.error('Lütfen önce sipariş numarası ve e-posta adresini girin.');
                        // reCAPTCHA'yı sıfırla
                        if (recaptchaRef.current) {
                          recaptchaRef.current.reset();
                        }
                        return;
                      }
                      setCaptchaToken(value);
                    }}
                    theme="light"
                  />
                </div>
              ) : !otpSent ? (
                <div style={{ textAlign: 'center', padding: '10px' }}>
                  <p style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>
                    {orderNumber && email ? 'Doğrulama kodu gönderiliyor...' : 'Lütfen sipariş numarası ve e-posta adresini girin.'}
                  </p>
                </div>
              ) : !otpVerified ? (
                <div style={{ width: '100%' }}>
                  <Form.Group className="custom-input-container" controlId="formOtpCode">
                    <Form.Control
                      type="text"
                      className="custom-placeholder otp-input"
                      placeholder="Doğrulama Kodu"
                      value={otpCode}
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                        setOtpCode(value);
                      }}
                      maxLength={6}
                      required
                      style={{ textAlign: 'center', fontSize: '18px', letterSpacing: '4px', fontWeight: '600' }}
                    />
                  </Form.Group>
                  <Button 
                    className="my-button-style" 
                    type="button"
                    onClick={handleVerifyOtp}
                    disabled={!otpCode || otpCode.length !== 6 || verifyingOtp}
                    style={{ width: '100%', marginTop: '10px' }}
                  >
                    {verifyingOtp ? 'Doğrulanıyor...' : 'Doğrula'}
                  </Button>
                  <Button 
                    variant="link" 
                    type="button"
                    onClick={handleResendOtp}
                    disabled={sendingOtp}
                    style={{ width: '100%', marginTop: '10px', fontSize: '13px', color: '#666' }}
                  >
                    {sendingOtp ? 'Gönderiliyor...' : 'Kodu Tekrar Gönder'}
                  </Button>
                </div>
              ) : (
                <div style={{ textAlign: 'center', padding: '10px' }}>
                  <p style={{ fontSize: '14px', color: '#28a745', marginBottom: '10px' }}>
                    ✓ E-posta doğrulandı, yönlendiriliyorsunuz...
                  </p>
                </div>
              )}
            </div>
          </Form>
        </Card.Body>
      </Card>
    </Container>
  );
};

export default Home;
